/**
 * The one effect-type list (RESPONDER_BADGE_WANTLIST_CLOSET_PLAN.md §4, 2026-10-18).
 *
 * Until now this list was copy-pasted into four files (AddPedalForm, AddWantForm,
 * SearchResults, LatestPedals), which is how the lightweight-listing work found it.
 * Every chip row, filter and the Reverb prefill below read from here.
 *
 * Order is the display order of the chips. Keep 'Other' last.
 */
export const EFFECT_TYPES = [
  'Bit Crusher', 'Boost', 'Buffer', 'Cab Sim', 'Chorus', 'Compressor',
  'Delay', 'Distortion', 'Drum Machine', 'Envelope Filter', 'EQ', 'Flanger',
  'Fuzz', 'Looper', 'Multi FX', 'Octave', 'Overdrive', 'Phaser',
  'Pitch Shifter', 'Reverb', 'Ring Mod', 'Synth', 'Tape Saturation',
  'Tremolo', 'Tuner', 'Uni-Vibe', 'Utility', 'Vibrato', 'Vol/Exp', 'Wah', 'Other',
];

/**
 * Reverb price-guide category slug -> PXC effect types to pre-tick on the listing
 * form when the member picks a typeahead suggestion.
 *
 * Reverb's /api/csps results carry the product's category in the
 * `new_search` / `used_search` link (`category=overdrive-and-boost`); the
 * suggestions function (functions-suggestions/searchReverbSuggestions.js)
 * extracts it as `category`. Reverb has 29 effects subcategories (listed from
 * /api/categories/flat, 2026-10-18). A category may map to two of ours; the chips
 * stay visible and editable, so a wrong second guess costs one tap.
 *
 * Deliberately NOT mapped (member picks): bass-pedals, vocal, noise-generators.
 * No Reverb equivalent at all: Bit Crusher, Drum Machine, Tape Saturation, Uni-Vibe.
 */
export const REVERB_CATEGORY_TO_EFFECT_TYPES = Object.freeze({
  'overdrive-and-boost': ['Overdrive', 'Boost'],
  'distortion': ['Distortion'],
  'fuzz': ['Fuzz'],
  'delay': ['Delay'],
  'reverb': ['Reverb'],
  'chorus-and-vibrato': ['Chorus', 'Vibrato'],
  'flanger': ['Flanger'],
  'phase-shifters': ['Phaser'],
  'tremolo': ['Tremolo'],
  'octave-and-pitch': ['Octave', 'Pitch Shifter'],
  'compression-and-sustain': ['Compressor'],
  'eq': ['EQ'],
  'wahs-and-filters': ['Wah', 'Envelope Filter'],
  'controllers-volume-and-expression': ['Vol/Exp'],
  'loop-pedals-and-samplers': ['Looper'],
  'multi-effect-unit': ['Multi FX'],
  'guitar-synths': ['Synth'],
  'ring-modulators': ['Ring Mod'],
  'tuning-pedals': ['Tuner'],
  'buffer': ['Buffer'],
  'cabinet-simulators': ['Cab Sim'],
  'amp-simulators': ['Cab Sim'],
  'preamps': ['Utility'],
  'noise-and-reduction-gates': ['Utility'],
  'pedalboards-and-power-supplies': ['Utility'],
});

/** Only products filed under Reverb's pedals root get a prefill. */
export const REVERB_PEDALS_ROOT = 'effects-and-pedals';

/**
 * Effect types to pre-tick for a Reverb suggestion, or [] when there is nothing
 * trustworthy to prefill (unknown category, or a product outside the pedals root
 * such as a Line 6 Helix, which Reverb files under amps).
 *
 * @param {string|null|undefined} category  Reverb category slug
 * @param {string|null|undefined} rootCategory  Reverb root_category_slug
 * @returns {string[]} subset of EFFECT_TYPES
 */
export function effectTypesForReverbCategory(category, rootCategory) {
  if (rootCategory && rootCategory !== REVERB_PEDALS_ROOT) return [];
  if (!category) return [];
  const mapped = REVERB_CATEGORY_TO_EFFECT_TYPES[String(category).toLowerCase()];
  return mapped ? [...mapped] : [];
}
